import { useCallback, useEffect, useMemo, useState } from 'react';
import { InspectionTemplateItemModel } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import {
  genericSearchPhraseValidation,
  normalizeForSearch,
} from 'shared/utils/search';
import { useSearch } from 'hooks/search/useSearch';

export interface UseTemplateListSearchResult {
  filteredItems: InspectionTemplateItemModel[];
  searchPhrase: string;
  searchChange: (phrase: string) => void;
  searchClear: () => void;
  debouncedSearchPhrase: string;
}

export function useTemplateListSearch(
  items: InspectionTemplateItemModel[]
): UseTemplateListSearchResult {
  const [debouncedSearchPhrase, setDebouncedSearchPhrase] = useState('');

  const handleDebouncedSearchChange = useCallback((phrase: string) => {
    setDebouncedSearchPhrase(phrase);
  }, []);

  const { searchChange, searchClear, searchPhrase } = useSearch(
    handleDebouncedSearchChange,
    LocalStorageManager.getTemplateSearchPhrase(),
    genericSearchPhraseValidation
  );

  useEffect(() => {
    LocalStorageManager.setTemplateSearchPhrase(searchPhrase);
  }, [searchPhrase]);

  const filteredItems = useMemo(() => {
    const normalizedPhrase = normalizeForSearch(debouncedSearchPhrase);
    return items.filter((template) =>
      template.searchString.includes(normalizedPhrase)
    );
  }, [items, debouncedSearchPhrase]);

  return {
    filteredItems,
    searchPhrase,
    searchChange,
    searchClear,
    debouncedSearchPhrase,
  };
}
